/* eslint-disable react/prop-types */
import React, { useContext, useMemo } from 'react';
import { getEditorPreloader } from '@confluence-classic/confluence-fabric-editor/src/lib';
import { LazyLoader as LazyEditPageLoader } from '@confluence-classic/confluence-fabric-editor/src/components/EditPage';

import { Attribution, ErrorBoundary } from '@confluence/error-boundary';
import { EditPageLoader } from '@confluence/edit-page-loader';
import { EditPageLoaderServer } from '@confluence/edit-page-loader/entry-points/EditPageLoaderServer';
import { getUniquePageLoadId } from '@confluence/unique-page-load-id';
import {
	CONTAINER_BLOG_ID,
	CONTAINER_HOME_ID,
	PRODUCT_HOME_ID,
	CONTAINER_HOME_LOADING_ID,
} from '@confluence/app-navigation/entry-points/navigationIDs';
import { shouldShowMobileWeb } from '@confluence/mobile-detection';
import { Screen } from '@confluence/screen';
import { MainLayout } from '@confluence/main-layout';
import { isCompanyHubSpaceKey } from '@confluence/route-manager/entry-points/companyHubUtils';
import {
	VIEW_PAGE,
	VIEW_BLOG_DATE_LEGACY,
	VIEW_BLOG,
	WHITEBOARD_BOARD,
	DATABASE_VIEW,
	EMBED_VIEW,
	FOLDER_VIEW,
	CREATE_CONTENT,
} from '@confluence/named-routes';
import { useIsEmbeddedConfluence_DO_NOT_USE } from '@confluence/embedded-confluence/entry-points/useIsEmbeddedConfluence';
import { PageShownPublicLinksEventLoader } from '@confluence/external-share-utils/entry-points/PageShownPublicLinksEventLoader';
import { BannerContext } from '@confluence/banners';
import { END } from '@confluence/navdex';
import { CreateContentRouteComponentLoader } from '@confluence/create-blank-fabric-page/entry-points/CreateContentRouteComponent';

import { ClassicEditorContextProviderLoader } from '../../ClassicEditorContextProvider';

import { WhiteboardRouteComponentLoader } from './WhiteboardRouteComponentLoader';
import { DatabaseRouteLoader } from './DatabaseRouteLoader';
import { EmbedRouteLoader } from './EmbedRouteLoader';
import { ViewPageRouteComponentLoader } from './ViewPageRouteComponentLoader';
import { FolderRouteLoader } from './FolderRouteLoader';

export const ViewOrEditRoute = ({
	name: routeName,
	params: { contentId, spaceKey, contentSlug, contentType: editContentType },
	query,
}) => {
	const { includeTopNav } = useContext(BannerContext);
	const isEmbeddedConfluence = useIsEmbeddedConfluence_DO_NOT_USE();
	const showPremiumOnboarding = !isEmbeddedConfluence;

	const isMobile = shouldShowMobileWeb();
	const isViewPage = routeName === VIEW_PAGE.name;
	const isViewBlog = routeName === VIEW_BLOG_DATE_LEGACY.name || routeName === VIEW_BLOG.name;
	const isWhiteboard = routeName === WHITEBOARD_BOARD.name;
	const isDatabase = routeName === DATABASE_VIEW.name;
	const isEmbed = routeName === EMBED_VIEW.name;
	const isBlog = editContentType === 'blog' || isViewBlog;
	const isFolder = routeName === FOLDER_VIEW.name;
	const isCreateContentRoute = routeName === CREATE_CONTENT.name;

	const isView = isViewPage || isViewBlog;
	const isPageLikeContent = isView || editContentType === 'pages' || editContentType === 'blog';

	// spaceKey comes from query object while on creation route
	const currentSpaceKey = spaceKey || query?.spaceKey;

	let viewId = CONTAINER_HOME_ID;

	if (isCreateContentRoute && !query.spaceKey) {
		// CREATE_CONTENT route can return an undefined spaceKey during creation so we render a loading screen
		viewId = CONTAINER_HOME_LOADING_ID;
	} else if (isCompanyHubSpaceKey(spaceKey)) {
		viewId = PRODUCT_HOME_ID;
	} else if (isBlog) {
		viewId = CONTAINER_BLOG_ID;
	}

	let contentType;
	if (editContentType === 'blog' || isViewBlog) {
		contentType = 'blogpost';
	} else if (editContentType === 'pages' || isViewPage) {
		contentType = 'page';
	} else if (isWhiteboard) {
		contentType = 'whiteboard';
	} else if (isDatabase) {
		contentType = 'database';
	} else if (isEmbed) {
		contentType = 'embed';
	} else if (isFolder) {
		contentType = 'folder';
	}

	const getScreenEvent = () => {
		if (isWhiteboard) {
			return {
				name: 'whiteboardScreen',
				id: contentId,
				attributes: {
					contentId,
				},
			};
		} else if (isDatabase) {
			return {
				name: 'databaseScreen',
				id: contentId,
				attributes: {
					contentId,
				},
			};
		} else if (isEmbed) {
			return {
				name: 'smartLinkEmbedPageScreen',
				id: contentId,
				attributes: {
					contentId,
					contentType: 'embed',
				},
			};
		} else if (isFolder) {
			return {
				name: 'folderScreen',
				id: contentId,
				attributes: {
					contentId,
				},
			};
		} else if (isView) {
			return {
				name: 'viewPageScreen',
				id: contentId,
				attributes: {
					isPublicView: false,
					pageLoadInfo: getUniquePageLoadId(),
				},
			};
		} else if (isCreateContentRoute) {
			return {
				name: 'createContentScreen',
			};
		} else {
			return {
				name: 'editPageScreen',
				id: contentId,
				attributes: {
					isPublicView: false,
					pageLoadInfo: getUniquePageLoadId(),
					navdexPointType: END,
				},
			};
		}
	};

	const getPageState = () => {
		const pageState = {
			spaceKey: currentSpaceKey,
			routeName,
		};

		if (!isCreateContentRoute) {
			pageState.contentId = contentId;
			pageState.contentType = contentType;
		}

		if (isView) {
			pageState.contentSlug = contentSlug;
		}

		return pageState;
	};

	const renderContent = () => {
		if (isWhiteboard) {
			return (
				<WhiteboardRouteComponentLoader
					spaceKey={spaceKey}
					contentId={contentId}
					isEmbeddedConfluence={isEmbeddedConfluence}
				/>
			);
		} else if (isDatabase) {
			return <DatabaseRouteLoader spaceKey={spaceKey} contentId={contentId} />;
		} else if (isEmbed) {
			return <EmbedRouteLoader spaceKey={spaceKey} contentId={contentId} />;
		} else if (isFolder) {
			return <FolderRouteLoader contentId={contentId} />;
		} else if (isView) {
			return (
				<ViewPageRouteComponentLoader
					routeName={routeName}
					spaceKey={spaceKey}
					contentId={contentId}
					contentSlug={contentSlug}
					isMobile={isMobile}
					isPage={isViewPage}
					isEmbeddedConfluence={isEmbeddedConfluence}
				/>
			);
		} else if (isCreateContentRoute) {
			return (
				<CreateContentRouteComponentLoader
					preloadWhiteboardRouteComponent={WhiteboardRouteComponentLoader.preload}
					preloadDatabaseRouteComponent={DatabaseRouteLoader.preload}
					preloadPageAndBlogRouteComponent={LazyEditPageLoader.preload}
					preloadSmartLinkEmbedRouteComponent={EmbedRouteLoader.preload}
				/>
			);
		} else {
			return (
				<>
					<ErrorBoundary
						attribution={Attribution.PAGE_MODES}
						attributes={{
							errorBoundaryId: 'ViewOrEditRoute-EditPageLoaderServer',
						}}
					>
						<EditPageLoaderServer />
					</ErrorBoundary>
					<ErrorBoundary
						attribution={Attribution.PAGE_MODES}
						attributes={{
							errorBoundaryId: 'ViewOrEditRoute-EditPageLoader',
						}}
					>
						<EditPageLoader
							contentId={contentId}
							spaceKey={spaceKey}
							draftShareId={query?.draftShareId}
							contentType={contentType}
							ClassicEditorContextProviderLoader={ClassicEditorContextProviderLoader}
						/>
					</ErrorBoundary>
					<ErrorBoundary
						attribution={Attribution.PAGE_MODES}
						attributes={{
							errorBoundaryId: 'ViewOrEditRoute-PageShownPublicLinksEventLoader',
						}}
					>
						<PageShownPublicLinksEventLoader
							contentId={contentId}
							pageType={contentType}
							isEditPage
						/>
					</ErrorBoundary>
				</>
			);
		}
	};

	const bannerContextValue = useMemo(
		() => ({
			includeTopNav: isEmbeddedConfluence ? false : includeTopNav,
		}),
		[includeTopNav, isEmbeddedConfluence],
	);

	return (
		<Screen
			screenEvent={getScreenEvent()}
			pageState={getPageState()}
			showPremiumOnboarding={isPageLikeContent ? showPremiumOnboarding : false}
		>
			<ErrorBoundary
				attribution={Attribution.PAGE_MODES}
				attributes={{
					errorBoundaryId: 'ViewOrEditRoute-MainLayout',
				}}
			>
				<MainLayout
					enableNavigation={!isMobile}
					navView={viewId}
					spaceKey={spaceKey}
					editorPreloader={isPageLikeContent ? getEditorPreloader : undefined}
					disableMinWidth={!!editContentType} // Disable min width for edit page
					isViewPage
					isEdit={!!editContentType}
				>
					<BannerContext.Provider value={bannerContextValue}>
						{renderContent()}
					</BannerContext.Provider>
				</MainLayout>
			</ErrorBoundary>
		</Screen>
	);
};
